import { Translation } from '@teamleader/i18n';
import cx from 'classnames';
import React, { useCallback, useMemo } from 'react';

import styles from './styles.css';
import Box, { BoxProps } from '../../../Components/UI/Box';
import Button from '../../../Components/UI/Button';
import TeamleaderIcon from '../../../Svg/25x18_logo.svg';
import LogoIcon from '../../../Svg/39x27_logo.svg';
import TeamleaderText from '../../../Svg/78x12_teamleader.svg';
import { EVENTS, track } from '../../../Utils/analytics';
import { TYPES } from '../../../Utils/jwt';

enum UTM_SOURCES {
  CLOUDSIGN = 'teamleader+cloudsign',
  INVOICECLOUD = 'teamleader+invoicecloud',
}

const TYPES_TO_UTM_SOURCES_MAP: Partial<Record<TYPES, UTM_SOURCES>> = {
  [TYPES.DEAL]: UTM_SOURCES.CLOUDSIGN,
  [TYPES.INVOICE]: UTM_SOURCES.INVOICECLOUD,
};

const BRANDING: Partial<Record<TYPES, Record<string, { url: string }>>> = {
  [TYPES.DEAL]: {
    'be-nl': {
      url: 'https://www.teamleader.be/nl-be/c/cloud',
    },
    'be-fr': {
      url: 'https://www.teamleader.be/fr-be/c/cloud',
    },
    nl: {
      url: 'https://www.teamleader.nl/c/cloud',
    },
    fr: {
      url: 'https://www.teamleader.fr/c/cloud',
    },
    it: {
      url: 'https://www.teamleadercrm.it/c/cloud',
    },
    es: {
      url: 'https://www.teamleader.es/c/cloud',
    },
    de: {
      url: 'https://www.teamleader.de/c/cloud',
    },
    eu: {
      url: 'https://www.teamleader.eu/c/cloud',
    },
  },
  [TYPES.INVOICE]: {
    'be-nl': {
      url: 'https://www.teamleader.be/nl-be/c/invoicecloud-referral',
    },
    'be-fr': {
      url: 'https://www.teamleader.be/fr-be/c/invoicecloud-referral',
    },
    nl: {
      url: 'https://www.teamleader.nl/c/invoicecloud-referral',
    },
    fr: {
      url: 'https://www.teamleader.fr/c/invoicecloud-referral',
    },
    it: {
      url: 'https://www.teamleadercrm.it/c/invoicecloud-referral',
    },
    es: {
      url: 'https://www.teamleader.es/c/invoicecloud-referral',
    },
    de: {
      url: 'https://www.teamleader.de/c/invoicecloud-referral',
    },
    eu: {
      url: 'https://www.teamleader.eu/c/invoicecloud-referral',
    },
  },
};

interface BrandingProps extends BoxProps {
  size?: 'small' | 'medium';
  theme?: 'light' | 'dark';
  language?: string | null;
  country?: string | null;
  type?: TYPES | null;
  className?: string;
}

const Branding: React.FC<BrandingProps> = ({
  size = 'medium',
  theme = 'dark',
  language,
  country,
  type,
  className,
  ...rest
}) => {
  const utmMedium = 'referral';
  const utmCampaign = 'cloudplatform+branding';

  const handleClickBranding = useCallback(() => track(EVENTS.CLICKED_BRANDING), []);

  const url = useMemo(() => {
    const branding = BRANDING[type as keyof typeof BRANDING] || BRANDING[TYPES.DEAL]!;
    const transformedCountry = country?.toLowerCase();
    let key = 'eu';

    if (transformedCountry === 'be' && language === 'nl') {
      key = 'be-nl';
    } else if (transformedCountry === 'be' && language === 'fr') {
      key = 'be-fr';
    } else if (transformedCountry && branding[transformedCountry]) {
      key = transformedCountry;
    } else if (language && branding[language]) {
      key = language;
    }

    const { url } = branding[key];
    const utmSource = TYPES_TO_UTM_SOURCES_MAP[type as keyof typeof TYPES_TO_UTM_SOURCES_MAP] || 'teamleader+cloud';
    return `${url}?utm_medium=${utmMedium}&utm_source=${utmSource}&utm_campaign=${utmCampaign}`;
  }, [language, country, type]);

  return (
    <Box display="flex" justifyContent="center" className={cx(styles['branding'], className)} margin={4} {...rest}>
      <Button
        element="a"
        href={url}
        onClick={handleClickBranding}
        target="_blank"
        rel="noopener noreferrer"
        padding={4}
        className={cx(styles['powered-by'], styles[`powered-by--${theme}`], {
          [styles['powered-by--small']]: size === 'small',
        })}
      >
        {size === 'medium' && (
          <Translation
            id="poweredBy"
            values={{
              link: (): React.ReactNode => (
                <>
                  <TeamleaderText className={styles['icon']} />
                  &nbsp;
                  <TeamleaderIcon className={styles['icon']} />
                </>
              ),
            }}
          />
        )}
        {size !== 'medium' && <Box element={LogoIcon} className={styles['icon']} />}
      </Button>
    </Box>
  );
};

export default Branding;
